import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, completeMfaLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Set once the password step succeeded on an account that has 2FA.
  const [mfaChallenge, setMfaChallenge] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { mfaChallenge: challenge } = await login(email, password);
      if (challenge) {
        setMfaChallenge(challenge);
        return;
      }
      navigate('/');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Anmeldung fehlgeschlagen.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfaSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await completeMfaLogin(mfaChallenge!, mfaCode);
      navigate('/');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Code ungültig.';
      setError(msg);
      setMfaCode('');
    } finally {
      setSubmitting(false);
    }
  }

  if (mfaChallenge) {
    return (
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <img src="/juni-logo.svg" alt="juni" className="h-8 mx-auto mb-8" />
          <div className="bg-surface border border-slate-200 rounded-xl shadow-sm p-6">
            <h1 className="text-lg font-semibold text-slate-900 mb-1">
              Bestätigung in zwei Schritten
            </h1>
            <p className="text-sm text-slate-500 mb-5">
              Geben Sie den Code aus Ihrer Authenticator-App ein.
            </p>
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                  {error}
                </div>
              )}
              <div>
                <input
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Alternativ einen Ihrer Wiederherstellungscodes eingeben.
                </p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-brand-600 hover:bg-brand-700 text-white font-medium text-sm py-2.5 rounded-lg transition-colors disabled:opacity-60"
              >
                {submitting ? 'Wird geprüft…' : 'Bestätigen'}
              </button>
              <button
                type="button"
                onClick={() => { setMfaChallenge(null); setMfaCode(''); setError(''); }}
                className="w-full text-center text-sm text-slate-500 hover:text-slate-700"
              >
                Abbrechen
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <img src="/juni-logo.svg" alt="juni" className="h-8 mx-auto mb-8" />
        <div className="bg-surface border border-slate-200 rounded-xl shadow-sm p-6">
          <h1 className="text-lg font-semibold text-slate-900 mb-1">Anmelden</h1>
          <p className="text-sm text-slate-500 mb-5">Zugang zu Ihren Produkten und Verträgen.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                {error}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Passwort</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-brand-600 hover:bg-brand-700 text-white font-medium text-sm py-2.5 rounded-lg transition-colors disabled:opacity-60"
            >
              {submitting ? 'Wird angemeldet…' : 'Anmelden'}
            </button>
          </form>

          <div className="flex justify-between mt-4 text-sm">
            <Link to="/forgot-password" className="text-brand-600 hover:text-brand-800">
              Passwort vergessen?
            </Link>
            <Link to="/register" className="text-brand-600 hover:text-brand-800">
              Konto erstellen
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
